// =============================================================================
// Resource Form Modal - Creazione/Modifica Risorsa
// =============================================================================

import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { DatePickerInput } from '../ui/DatePickerInput';
import { Resource, ResourceType, AttributeFieldDefinition } from '../../types';
import { useCreateResource, useUpdateResource } from '../../api/hooks';
import { useToast } from '../../store/toastStore';
import { useTranslatedError } from '../../hooks/useTranslatedError';

// Color presets
const COLOR_PRESETS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

// Zod Schema
const resourceSchema = z.object({
  name: z.string().min(1, i18n.t('validation.nameRequired', { ns: 'settings' })),
  resourceTypeId: z.string().min(1, i18n.t('validation.resourceTypeRequired', { ns: 'settings' })),
  code: z.string().optional(),
  description: z.string().optional(),
  color: z.string().optional(),
  isBookable: z.boolean(),
  isPubliclySelectable: z.boolean(),
  customAttributes: z.record(z.string(), z.unknown()).optional(),
});

type ResourceFormData = z.infer<typeof resourceSchema>;

interface ResourceFormModalProps {
  resource: Resource | null;
  resourceTypes: ResourceType[];
  preselectedTypeId?: string | null;
  onClose: () => void;
}

export function ResourceFormModal({ resource, resourceTypes, preselectedTypeId, onClose }: ResourceFormModalProps) {
  const createResource = useCreateResource();
  const updateResource = useUpdateResource();
  const isEditing = !!resource;
  const { t } = useTranslation('settings');
  const toast = useToast();
  const { getErrorMessage } = useTranslatedError();

  // Determina il tipo preselezionato: da resource (modifica) oppure da sidebar (creazione)
  const initialTypeId = resource?.resourceTypeId ?? preselectedTypeId ?? '';

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResourceFormData>({
    resolver: zodResolver(resourceSchema),
    defaultValues: {
      name: resource?.name ?? '',
      resourceTypeId: initialTypeId,
      code: resource?.code ?? '',
      description: resource?.description ?? '',
      color: resource?.color ?? COLOR_PRESETS[0],
      isBookable: resource?.isBookable ?? true,
      isPubliclySelectable: resource?.isPubliclySelectable ?? false,
      customAttributes: (resource?.customAttributes as Record<string, unknown>) ?? {},
    },
  });

  const selectedTypeId = watch('resourceTypeId');
  const selectedColor = watch('color');

  // Get attributes schema for selected type (now array format)
  const selectedType = useMemo(() => {
    return resourceTypes.find(t => t.id === selectedTypeId);
  }, [resourceTypes, selectedTypeId]);

  const attributesSchema = useMemo(() => {
    if (!selectedType?.attributesSchema) return [];
    // Supporta sia il nuovo formato array che il vecchio formato object
    if (Array.isArray(selectedType.attributesSchema)) {
      return selectedType.attributesSchema as AttributeFieldDefinition[];
    }
    // Backward compatibility: converti oggetto in array
    return Object.entries(selectedType.attributesSchema).map(([key, schema]) => ({
      key,
      label: (schema as any).label || key,
      type: (schema as any).type || 'text',
      required: (schema as any).required || false,
      placeholder: (schema as any).placeholder,
      options: (schema as any).options,
    })) as AttributeFieldDefinition[];
  }, [selectedType]);

  // Reset custom attributes when type changes
  useEffect(() => {
    if (!isEditing && selectedTypeId) {
      setValue('customAttributes', {});
    }
  }, [selectedTypeId, isEditing, setValue]);

  const onSubmit = async (data: ResourceFormData) => {
    try {
      if (isEditing && resource) {
        await updateResource.mutateAsync({ id: resource.id, ...data });
      } else {
        await createResource.mutateAsync(data);
      }
      onClose();
    } catch (error) {
      const errorCode =
        error instanceof AxiosError ? (error.response?.data?.errorCode as string | undefined) : undefined;
      const message = getErrorMessage(error);
      // Il conflitto sul codice è contestuale al campo: mostralo inline sotto l'input.
      if (errorCode === 'RESOURCE_CODE_EXISTS') {
        setError('code', { type: 'server', message });
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing ? t('resources.editTitle') : t('resources.createTitle')}
            </h3>
            <button
              onClick={onClose}
              aria-label={t('common:buttons.close')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('resources.nameRequired')}
              </label>
              <input
                type="text"
                {...register('name')}
                className="w-full px-3 py-2 input"
                placeholder={t('resources.namePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Tipo Risorsa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('resources.typeRequired')}
              </label>
              <select
                {...register('resourceTypeId')}
                className="w-full px-3 py-2 input"
              >
                <option value="">{t('resources.typePlaceholder')}</option>
                {resourceTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.resourceTypeId && (
                <p className="mt-1 text-sm text-red-600">{errors.resourceTypeId.message}</p>
              )}
            </div>

            {/* Codice */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('resources.codeOptional')}
              </label>
              <input
                type="text"
                {...register('code')}
                className="w-full px-3 py-2 input"
                placeholder={t('resources.codePlaceholder')}
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            {/* Descrizione */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('resources.descriptionOptional')}
              </label>
              <textarea
                {...register('description')}
                rows={2}
                className="w-full px-3 py-2 input"
                placeholder={t('resources.descriptionPlaceholder')}
              />
            </div>

            {/* Colore */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common:fields.color')}
              </label>
              <div className="flex items-center gap-3">
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setValue('color', color)}
                      className={`w-8 h-8 rounded-full transition-transform ${
                        selectedColor === color ? 'ring-2 ring-offset-2 ring-emerald-500 scale-110' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  {...register('color')}
                  className="w-8 h-8 rounded cursor-pointer"
                />
              </div>
            </div>

            {/* Prenotabile */}
            <div className="flex items-center gap-3">
              <Controller
                name="isBookable"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                )}
              />
              <label className="text-sm text-gray-700">
                {t('resources.bookable')}
              </label>
            </div>

            {/* Selezionabile pubblicamente */}
            <div className="flex items-start gap-3">
              <Controller
                name="isPubliclySelectable"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                )}
              />
              <label className="text-sm text-gray-700">
                <span className="font-medium">{t('resources.publiclySelectable')}</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {t('resources.publiclySelectableHint')}
                </span>
              </label>
            </div>

            {/* Attributi Dinamici basati sul tipo selezionato */}
            {attributesSchema.length > 0 && (
              <div className="border-t border-gray-200 pt-5">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  {t('resources.attributesTitle', { type: selectedType?.name })}
                </h4>
                <div className="space-y-4">
                  {attributesSchema.map((fieldDef) => (
                    <DynamicAttributeField
                      key={fieldDef.key}
                      fieldDef={fieldDef}
                      control={control}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary flex items-center gap-2 px-4 py-2 text-sm"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEditing ? t('common:buttons.saveChanges') : t('resources.createButton')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Dynamic Attribute Field Component
// =============================================================================

interface DynamicAttributeFieldProps {
  fieldDef: AttributeFieldDefinition;
  control: any;
}

function DynamicAttributeField({ fieldDef, control }: DynamicAttributeFieldProps) {
  const { key, label, type, required, placeholder, options } = fieldDef;
  const { t } = useTranslation('settings');

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Controller
        name={`customAttributes.${key}`}
        control={control}
        render={({ field }) => {
          switch (type) {
            case 'select':
              return (
                <select
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  className="w-full px-3 py-2 input"
                >
                  <option value="">{t('resources.selectPlaceholder')}</option>
                  {options?.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              );
            case 'boolean':
              return (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-600">{t('services.yes')}</span>
                </div>
              );
            case 'number':
              return (
                <input
                  type="number"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.valueAsNumber || undefined)}
                  placeholder={placeholder}
                  className="w-full px-3 py-2 input"
                />
              );
            case 'date':
              return (
                <DatePickerInput
                  value={field.value ?? ''}
                  onChange={(val) => field.onChange(val)}
                />
              );
            case 'email':
              return (
                <input
                  type="email"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder={placeholder || t('placeholders.emailExample')}
                  className="w-full px-3 py-2 input"
                />
              );
            case 'phone':
              return (
                <input
                  type="tel"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder={placeholder || t('placeholders.phoneExample')}
                  className="w-full px-3 py-2 input"
                />
              );
            default: // 'text'
              return (
                <input
                  type="text"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder={placeholder}
                  className="w-full px-3 py-2 input"
                />
              );
          }
        }}
      />
    </div>
  );
}
